import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Send, Loader2, Plus, Trash2, Eye, ListChecks, Users } from "lucide-react";

const fmtDateTime = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

/**
 * Admin card for Resend transactional email.
 * - List templates + send a single test email
 * - Bulk-send the "Friends launch" template to a hand-picked recipient list
 * - View recent send log (status / errors)
 */
export default function EmailCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("test");
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // Test send
  const [testTo, setTestTo] = useState("");
  const [testTemplate, setTestTemplate] = useState("friends_launch");
  const [testFirstName, setTestFirstName] = useState("Friend");
  const [sendingTest, setSendingTest] = useState(false);

  // Friends launch
  const [friends, setFriends] = useState([{ email: "", first_name: "" }]);
  const [sendingBatch, setSendingBatch] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email/templates");
      setInfo(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const loadLog = async () => {
    setLogLoading(true);
    try {
      const { data } = await api.get("/admin/email/log?limit=50");
      setLog(data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLogLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "log") loadLog(); }, [tab]);

  const sendTest = async () => {
    if (!testTo.includes("@")) { toast.error("Enter a valid email"); return; }
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/email/send-test", {
        to_email: testTo,
        template_id: testTemplate,
        vars: { first_name: testFirstName || "Friend" },
      });
      if (data.status === "sent") {
        toast.success(`Sent to ${testTo} · id ${(data.resend_id || "").slice(0, 8)}`);
      } else {
        toast.error(`Failed: ${data.error || "unknown"}`);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSendingTest(false); }
  };

  const addFriend = () => setFriends((p) => [...p, { email: "", first_name: "" }]);
  const removeFriend = (i) => setFriends((p) => p.filter((_, idx) => idx !== i));
  const updateFriend = (i, key, val) => setFriends((p) => p.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  const sendFriendsBatch = async () => {
    const valid = friends.filter((f) => f.email.includes("@") && f.first_name.trim());
    if (!valid.length) { toast.error("Add at least one recipient with email + name"); return; }
    if (!confirm(`Send the friends-launch email to ${valid.length} recipient${valid.length === 1 ? "" : "s"}?`)) return;
    setSendingBatch(true);
    try {
      const { data } = await api.post("/admin/email/send-friends-launch", {
        recipients: valid.map((f) => ({ email: f.email.trim(), first_name: f.first_name.trim() })),
      });
      toast.success(`${data.sent} sent · ${data.failed} failed`);
      if (data.failed > 0) {
        const errs = (data.results || []).filter((r) => r.status === "failed").map((r) => `${r.to}: ${r.error}`).join("\n");
        // Show first error to help debug
        if (errs) console.warn("Email failures:\n" + errs);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSendingBatch(false); }
  };

  const previewUrl = (file) => `/email-templates/${file}`;

  return (
    <div className="border hairline bg-white p-6 mb-8" data-testid="email-card">
      <header className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> Transactional email · Resend
          </div>
          <h3 className="font-display text-xl mt-1">Friends beta · launch emails</h3>
          {info && (
            <p className="text-xs text-[#666] mt-1 max-w-2xl">
              Sender · <code className="bg-[#fafafa] px-1">{info.sender}</code>
              {!info.configured && <span className="ml-2 text-[#dc2626] font-bold">RESEND_API_KEY missing</span>}
            </p>
          )}
        </div>
        <div className="flex gap-1">
          {[["test", "Test send"], ["batch", "Friends batch"], ["log", "Send log"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`text-xs mono uppercase tracking-wider px-3 py-2 border-2 transition-colors ${tab === k ? "bg-[#111] text-white border-[#111]" : "bg-white border-[#ddd] hover:border-[#111]"}`}
              data-testid={`email-tab-${k}`}
            >{label}</button>
          ))}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-[#888] gap-2 text-sm mono">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
        </div>
      ) : !info ? null : (
        <>
          {/* TAB · TEST SEND */}
          {tab === "test" && (
            <div className="space-y-4" data-testid="email-test-tab">
              <div className="bg-[#fafafa] border hairline p-4 grid grid-cols-1 sm:grid-cols-[1fr_220px_140px_auto] gap-3 items-end">
                <div>
                  <label className="eyebrow block mb-1.5">Send to</label>
                  <input
                    type="email"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="you@yourdomain.com"
                    className="w-full border hairline px-3 py-2 text-sm"
                    data-testid="email-test-to"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-1.5">Template</label>
                  <select
                    value={testTemplate}
                    onChange={(e) => setTestTemplate(e.target.value)}
                    className="w-full border hairline px-3 py-2 text-sm bg-white"
                    data-testid="email-test-template"
                  >
                    {info.templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-1.5">First name</label>
                  <input
                    type="text"
                    value={testFirstName}
                    onChange={(e) => setTestFirstName(e.target.value.slice(0, 60))}
                    className="w-full border hairline px-3 py-2 text-sm"
                    data-testid="email-test-firstname"
                  />
                </div>
                <button
                  onClick={sendTest}
                  disabled={sendingTest || !info.configured}
                  className="btn-primary text-xs px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="email-test-send"
                >
                  {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={2.5} />}
                  Send
                </button>
              </div>

              <div className="border hairline">
                {info.templates.map((t, i) => (
                  <div key={t.id} className={`flex items-center justify-between gap-3 px-3 py-2.5 ${i < info.templates.length - 1 ? "border-b hairline" : ""}`}>
                    <div className="min-w-0">
                      <div className="font-display text-sm">{t.subject}</div>
                      <div className="text-[11px] mono text-[#666] mt-0.5">{t.id} · placeholders: {t.placeholders.join(", ") || "none"}</div>
                      <div className="text-[10px] text-[#888] mt-0.5">{t.description}</div>
                    </div>
                    <a
                      href={previewUrl(t.file)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] mono uppercase tracking-wider px-3 py-1.5 border hairline hover:bg-[#fff4ee]"
                      data-testid={`email-preview-${t.id}`}
                    >
                      <Eye className="w-3 h-3" /> Preview
                    </a>
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-[#888]">
                Until you verify a custom domain on Resend, sends only deliver to the email you signed up to Resend with. Add a domain at <code className="bg-[#fafafa] px-1">resend.com → Domains</code> for full delivery.
              </p>
            </div>
          )}

          {/* TAB · FRIENDS BATCH */}
          {tab === "batch" && (
            <div className="space-y-4" data-testid="email-batch-tab">
              <div className="bg-[#fff4ee] border-2 border-[#ff4500]/40 p-4 text-xs leading-relaxed">
                <strong className="text-[#ff4500] uppercase tracking-wider mono">Friends launch · 1 batch only</strong>
                <p className="mt-1.5 text-[#444]">
                  Sends template <code className="bg-white px-1">friends_launch</code> ("I shipped the camping app — honest feedback?") to up to 50 hand-picked recipients. Adds <code>{`{{first_name}}`}</code> per row.
                  <strong> Don't blast it</strong> — start with 5 trusted mates.
                </p>
              </div>

              <div className="border hairline divide-y" data-testid="email-friends-list">
                {friends.map((f, i) => (
                  <div key={i} className="grid grid-cols-[1fr_180px_auto] gap-2 p-2.5 items-center" data-testid={`email-friend-row-${i}`}>
                    <input
                      type="email"
                      value={f.email}
                      onChange={(e) => updateFriend(i, "email", e.target.value)}
                      placeholder="friend@email.com"
                      className="border hairline px-3 py-2 text-sm"
                      data-testid={`email-friend-email-${i}`}
                    />
                    <input
                      type="text"
                      value={f.first_name}
                      onChange={(e) => updateFriend(i, "first_name", e.target.value.slice(0, 60))}
                      placeholder="First name"
                      className="border hairline px-3 py-2 text-sm"
                      data-testid={`email-friend-name-${i}`}
                    />
                    <button
                      onClick={() => removeFriend(i)}
                      disabled={friends.length === 1}
                      className="p-2 hover:bg-[#fee2e2] hover:text-[#dc2626] border hairline disabled:opacity-30"
                      aria-label="Remove recipient"
                      data-testid={`email-friend-remove-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 items-center justify-between">
                <button
                  onClick={addFriend}
                  className="text-xs mono uppercase tracking-wider px-3 py-2 border-2 border-[#ddd] bg-white hover:border-[#111] inline-flex items-center gap-2"
                  data-testid="email-friend-add"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add friend
                </button>
                <button
                  onClick={sendFriendsBatch}
                  disabled={sendingBatch || !info.configured}
                  className="btn-primary text-xs px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="email-friend-send-batch"
                >
                  {sendingBatch ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" strokeWidth={2.5} />}
                  Send to {friends.filter((f) => f.email.includes("@") && f.first_name.trim()).length} friends
                </button>
              </div>
            </div>
          )}

          {/* TAB · LOG */}
          {tab === "log" && (
            <div className="space-y-3" data-testid="email-log-tab">
              <div className="flex justify-between items-center">
                <div className="text-xs mono text-[#666]">Last {log.length} sends</div>
                <button
                  onClick={loadLog}
                  disabled={logLoading}
                  className="text-xs mono uppercase tracking-wider px-3 py-1.5 border hairline hover:bg-[#fafafa] inline-flex items-center gap-2"
                  data-testid="email-log-refresh"
                >
                  {logLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListChecks className="w-3 h-3" />}
                  Refresh
                </button>
              </div>
              {log.length === 0 ? (
                <div className="text-center py-6 text-sm text-[#666] bg-[#fafafa] border hairline">
                  No emails sent yet.
                </div>
              ) : (
                <div className="border hairline" data-testid="email-log-list">
                  {log.map((e, i) => (
                    <div key={e.log_id || i} className="grid grid-cols-[110px_1fr_auto] gap-3 items-center px-3 py-2 border-b hairline last:border-b-0 text-xs" data-testid={`email-log-row-${i}`}>
                      <span className={`mono uppercase tracking-wider px-2 py-0.5 inline-block text-center text-[10px] ${e.status === "sent" ? "bg-[#dcfce7] text-[#166534]" : "bg-[#fee2e2] text-[#991b1b]"}`}>
                        {e.status}
                      </span>
                      <div className="min-w-0">
                        <div className="truncate"><strong>{e.to}</strong> · {e.template_id || "—"}</div>
                        <div className="text-[10px] mono text-[#888] truncate">{e.subject}</div>
                        {e.error && <div className="text-[10px] text-[#dc2626] truncate">{e.error}</div>}
                      </div>
                      <span className="mono text-[10px] text-[#888]">{fmtDateTime(e.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
